import { useEffect, useState } from "react";
import { Container, InsightsReportSkeleton } from "@strivelabs/ui-blocks";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@strivelabs/ui-core";
import { InsightCardPayloadRenderer, InsightCardMultiPageRenderer } from "../ai-elements/block-renderers";
import type { InsightCardPayload, InsightCardPayloadV2, UIActionEvent } from "@/types/ui-blocks";

// Flat report example - v1 format
const payloadFlat: InsightCardPayload = {
  type: 'FLAT',
  ui: {
    blocks: [
      // Block 1: SQL Performance - Green positive card
      {
        id: "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
        type: "insight_card",
        version: "v1",
        config: {
          heading: "SQL Performance **exceeding targets** across all time periods",
          layout: {
            left: ["f1e2d3c4-b5a6-4978-8a1b-2c3d4e5f6a7b"],
            right: ["b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e", "c3d4e5f6-a7b8-4c9d-0e1f-2a3b4c5d6e7f"],
          },
        },
        block_elements: [
          {
            id: "f1e2d3c4-b5a6-4978-8a1b-2c3d4e5f6a7b",
            type: "line_chart",
            version: "v1",
            content: {
              title: "SQL Volume - Last 6 Months",
              data: [
                { month: "May", sqls: 145 },
                { month: "Jun", sqls: 168 },
                { month: "Jul", sqls: 182 },
                { month: "Aug", sqls: 195 },
                { month: "Sep", sqls: 210 },
                { month: "Oct", sqls: 243 },
              ],
              xAxisKey: "month",
              config: {
                sqls: { label: "SQLs", color: "#10b981" },
              },
              showLegend: false,
            },
          },
          {
            id: "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
            type: "text",
            version: "v1",
            content: `Strong upward trajectory across all metrics indicates **robust pipeline health**. Quarter-to-date performance is **16% ahead** of last quarter's pace, while monthly momentum shows **15.7% growth**. Year-to-date numbers confirm this is a **sustained trend**, not a temporary spike. **Continue current strategies** and prepare capacity for increased demo volume.`,
          },
          {
            id: "c3d4e5f6-a7b8-4c9d-0e1f-2a3b4c5d6e7f",
            type: "metrics_grid",
            version: "v1",
            content: {
              header: "SQL Performance Metrics",
              cells: [
                { value: "453", change: "+16%", label: "Q3 vs Q2", changeColor: "text-green-600" },
                { value: "243", change: "+15.7%", label: "Nov vs Oct", changeColor: "text-green-600" },
                { value: "453", change: "+18%", label: "First 21 days of Nov vs Oct", changeColor: "text-green-600" },
                { value: "1,143", change: "", label: "Year to Date", changeColor: "text-green-600" },
              ],
              columns: 3,
            },
          },
        ],
      },
      // Block 2: SQL Breakdown by Source - Yellow warning card
      {
        id: "e5f6a7b8-c9d0-4e1f-2a3b-4c5d6e7f8a9b",
        type: "insight_card",
        version: "v1",
        config: {
          heading: "SQL sources show **diverging performance** with ABM lagging",
          layout: {
            left: ["f6a7b8c9-d0e1-4f2a-3b4c-5d6e7f8a9b0c"],
            right: ["a7b8c9d0-e1f2-4a3b-4c5d-6e7f8a9b0c1d", "b8c9d0e1-f2a3-4b4c-5d6e-7f8a9b0c1d2e"],
          },
        },
        block_elements: [
          {
            id: "f6a7b8c9-d0e1-4f2a-3b4c-5d6e7f8a9b0c",
            type: "line_chart",
            version: "v1",
            content: {
              title: "SQL Volume by Source - Last 6 Months",
              data: [
                { month: "May", intent: 92, abm: 53 },
                { month: "Jun", intent: 105, abm: 63 },
                { month: "Jul", intent: 118, abm: 64 },
                { month: "Aug", intent: 128, abm: 67 },
                { month: "Sep", intent: 142, abm: 68 },
                { month: "Oct", intent: 165, abm: 78 },
              ],
              xAxisKey: "month",
              config: {
                intent: { label: "Intent", color: "#3b82f6" },
                abm: { label: "ABM", color: "#f59e0b" },
              },
              showLegend: true,
            },
          },
          {
            id: "a7b8c9d0-e1f2-4a3b-4c5d-6e7f8a9b0c1d",
            type: "text",
            version: "v1",
            content: `Intent continues its **strong growth trajectory** with LinkedIn Ads driving 42% of volume. However, ABM shows concerning **stagnation** with only marginal gains. **Healthcare Vertical** is the standout ABM program but can't compensate for underperformance elsewhere. Rep productivity metrics reveal the gap—**Intent reps averaging 5.5 SQLs** versus **ABM's 3.1 SQLs**. Time to **reassess ABM targeting** and **redistribute resources** to Intent.`,
          },
          {
            id: "b8c9d0e1-f2a3-4b4c-5d6e-7f8a9b0c1d2e",
            type: "metrics_grid",
            version: "v1",
            content: {
              header: "Source Performance Breakdown",
              cells: [
                { value: "165", change: "+16.2%", label: "Intent SQLs MTD", changeColor: "text-green-600" },
                { value: "78", change: "+14.7%", label: "ABM SQLs MTD", changeColor: "text-green-600" },
                { value: "LinkedIn Ads", change: "69 SQLs", label: "Top Intent Source", changeColor: "text-green-600" },
                { value: "Healthcare", change: "28 SQLs", label: "Top ABM Program", changeColor: "text-green-600" },
                { value: "5.5", change: "+8.9%", label: "Rep Productivity - Intent", changeColor: "text-green-600" },
                { value: "3.1", change: "+2.6%", label: "Rep Productivity - ABM", changeColor: "text-orange-600" },
              ],
              columns: 3,
            },
          },
        ],
      },
      // Block 3: Meetings Booked - Green positive card
      {
        id: "d0e1f2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a",
        type: "insight_card",
        version: "v1",
        config: {
          heading: "Meeting bookings **accelerating** with healthy pipeline ahead",
          layout: {
            left: ["e1f2a3b4-c5d6-4e7f-8a9b-0c1d2e3f4a5b"],
            right: ["c5d6e7f8-a9b0-4c1d-2e3f-4a5b6c7d8e9f", "f2a3b4c5-d6e7-4f8a-9b0c-1d2e3f4a5b6c"],
          },
        },
        block_elements: [
          {
            id: "e1f2a3b4-c5d6-4e7f-8a9b-0c1d2e3f4a5b",
            type: "bar_chart",
            version: "v1",
            content: {
              title: "Demo Meetings Booked - Last 8 Weeks",
              data: [
                { week: "Oct 5", meetings: 42 },
                { week: "Oct 12", meetings: 38 },
                { week: "Oct 19", meetings: 45 },
                { week: "Oct 26", meetings: 51 },
                { week: "Nov 2", meetings: 48 },
                { week: "Nov 9", meetings: 55 },
                { week: "Nov 16", meetings: 58 },
                { week: "Nov 23", meetings: 63 },
              ],
              xAxisKey: "week",
              dataKeys: [{ key: "meetings", name: "Meetings Booked" }],
              showLegend: false,
            },
          },
          {
            id: "c5d6e7f8-a9b0-4c1d-2e3f-4a5b6c7d8e9f",
            type: "text",
            version: "v1",
            content: `Exceptional **23.4% month-over-month growth** in demo bookings reflects the SQL performance gains translating into real pipeline velocity. The **8-week upward trend** shows consistent execution from the sales team, with Week 8 hitting 63 bookings—a **50% increase** from Week 1. With **127 meetings already scheduled**, the pipeline looks healthy for next month. **Ensure demo quality matches quantity** and maintain SDR/AE capacity to handle this volume.`,
          },
          {
            id: "f2a3b4c5-d6e7-4f8a-9b0c-1d2e3f4a5b6c",
            type: "metrics_grid",
            version: "v1",
            content: {
              header: "Meeting Booking Metrics",
              cells: [
                { value: "216", change: "+23.4%", label: "Total Meetings MTD", changeColor: "text-green-600" },
                { value: "127", change: "scheduled", label: "Upcoming Meetings", changeColor: "text-green-600" },
              ],
              columns: 2,
            },
          },
        ],
      },
    ],
  },
};

// Multi-page report example - v2 format with multi-page type
const payloadMultiPage: InsightCardPayloadV2 = {
  "type": "MULTI_PAGE",
  "version": "v2",
  "page_groups": [
    {
      "id": "0f63c091-a02e-49a9-8a97-37abfa93596c",
      "display_name": "Campaign Analysis",
      "pages": [
        {
          "id": "231e6dc0-ea81-43d9-90c1-89100eae1ef0",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "tags": ["US_Search_Product_Theme_Exact"],
                "heading": "**CTR** is down despite higher **Impressions**, indicating Dropping Engagement/CTR.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week **CTR** decreased while **Impressions** (+22%) and **Search Impression Share** (+3.2pp) improved, which points to an engagement-quality drop rather than a budget or rank issue; the improvement in **Search Rank Lost IS** (decrease of 3.2pp) supports this **rather than** competitive rank pressure.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Impressions",
                        "value": "2,459",
                        "change": "+22.00%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "103",
                        "change": "-9.60%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR",
                        "value": "4.51%",
                        "change": "-0.34 pp",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Impression Share",
                        "value": "78.15%",
                        "change": "+3.22 pp",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Rank Lost IS",
                        "value": "21.85%",
                        "change": "-3.22 pp",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Budget Lost IS",
                        "value": "0.00%",
                        "change": "0.00 pp",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Driving metrics \u2014 this week vs 8-week baseline",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "This week: **Impressions** = 2,459 (+22% vs 8-week baseline 2,015.5), **Clicks** = 103 (-9.6% vs baseline 113.9), **CTR** = 4.51% (down 0.34 pp vs baseline 4.85%), **Search Impression Share** = 78.15% (+3.22 pp vs baseline 74.92%), **Search Rank Lost IS** = 21.85% (improved 3.22 pp).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Pause or reduce bids for enabled keywords with Quality Score \u2264 2.\n- Set deliberate bids or pause enabled keywords with zero keyword-level bids.\n- Enable CRM campaign-source mapping and normalize campaign naming for attribution.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "US_Search_Product_Theme_Exact"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "16720484-39e3-4286-addb-01d0501214d2",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "tags": ["Brand_Everstage_Phrase_US"],
                "heading": "**Spend** rose sharply despite **Impression Share** remaining flat, indicating Abnormal Spend.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "The campaign's **spend** increased substantially vs its 8\u2011week baseline while **Impression Share** did not improve and **CTR** only rose modestly, which suggests higher cost to buy similar share *rather than* a planned budget increase.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$4,628.17",
                        "change": "+93.67%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "197",
                        "change": "+61.34%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "851",
                        "change": "+53.76%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "23.15%",
                        "change": "+5.50%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Conversions (Google Ads)",
                        "value": "8",
                        "change": "+146.15%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Impression Share",
                        "value": "92.99%",
                        "change": "-2.34%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics vs 8-week baseline"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "This week the campaign spent **$4,628** vs an 8\u2011week weekly average of **$2,389**. The single search term \"everstage\" accounted for **$3,797** of that spend and 170 clicks last week, while CRM conversion density for this campaign is recorded as **none**.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Reconcile CRM attribution and import offline conversions.\n- Reduce bids on non\u2011converting brand variants.\n- Cap bids for small\u2011volume, low\u2011performing keyword variants.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Brand_Everstage_Phrase_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Abnormal Spend"
            }
          ]
        },
        {
          "id": "0d928be9-88c4-4513-afd7-39de4bd38ef5",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "tags": ["All_themes_Phrase_US"],
                "heading": "**Spend** is scaling faster than **CRM conversions** despite stable CTR, indicating Abnormal Spend.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week the campaign's **spend** rose sharply while **CRM conversions** remained at zero and **CTR** held near baseline, so the primary issue is efficiency (more dollars without CRM lift) rather than an engagement problem. Rather than being driven by falling CTR or immediate budget caps, the signal points to increased spend without proportionate CRM outcomes.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$3,347.26",
                        "change": "+63.40%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "85",
                        "change": "+63.84%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "4,119",
                        "change": "+63.60%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "CTR",
                        "value": "2.06%",
                        "change": "-3.11%",
                        "changeColor": "text-gray-600"
                      },
                      {
                        "label": "CRM conversions",
                        "value": "0"
                      },
                      {
                        "label": "Search rank lost IS",
                        "value": "54.09%",
                        "change": "+11.83pp",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "For the week starting 2026-01-19 **spend** was **$3,347.26** vs the 8-week weekly avg of **$2,048.37**, **clicks** were **85** vs **51.88** avg, and **CRM conversions** were **0** (baseline **0**); additionally **search_rank_lost_impression_share** increased to **0.54** vs baseline **0.42**.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Add negatives for low-intent and competitor queries (use exact and phrase matches).\n- Lower bids or pause keywords with quality score 1 and high CPCs.\n- Hold aggressive pacing; reduce bids or shift budget until CRM attribution reconciled.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "All_themes_Phrase_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Med"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Abnormal Spend"
            }
          ]
        },
        {
          "id": "49243a94-6244-426a-9114-cce0919817b3",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "tags": ["US_Search_Product_CPQ_Exact"],
                "heading": "**Impressions** are down despite near\u2011baseline **Spend** and a spike in **Search Budget Lost IS**, indicating Budget Constrained.",
                "border_color": "red"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week the campaign delivered **fewer impressions** even though weekly **spend** stayed near the 8-week average, while **Search Budget Lost IS** rose sharply \u2014 this points to budget exhaustion during peak query periods rather than rank or relevance issues. Search Rank Lost IS fell versus baseline, supporting a budget (not rank) explanation.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend (last week)",
                        "value": "$1,209.68",
                        "change": "+4.00%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions (last week)",
                        "value": "1,817",
                        "change": "-15.90%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "1.43%",
                        "change": "+0.11 pp",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Budget Lost IS (last week)",
                        "value": "33.66%",
                        "change": "+18.96 pp",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Rank Lost IS (last week)",
                        "value": "10.18%",
                        "change": "-7.43 pp",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks (last week)",
                        "value": "26",
                        "change": "-2.80%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics (last week vs baseline)",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**Evidence:** Weekly aggregates show **Spend** roughly +4% vs the 8\u2011week weekly average while **Impressions** fell ~15.9%, and **Search Budget Lost IS** increased from ~0.147 to ~0.337 (absolute +0.189). **Search Rank Lost IS** decreased vs baseline, which supports budget pressure as the driver.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "**Recommended Actions**\n\n- Increase daily campaign budget to $350\u2013$450 to reduce budget exhaustion.\n- Raise bids for high-value ad groups; lower bids on low-priority groups.\n- Monitor Search Budget Lost IS and Impressions daily for seven days.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "US_Search_Product_CPQ_Exact"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "High"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Budget Constrained"
            }
          ]
        },
        {
          "id": "439f5b87-83cf-4ac2-890e-df4b0345c1d2",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "tags": ["Competitor_US_Brand"],
                "heading": "**Spend** is elevated despite stable **CTR**, indicating Abnormal Spend.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week\u2019s **spend** rose while **CTR** remained essentially flat and **search_impression_share** increased slightly, pointing to spend velocity rather than weaker ad relevance. This looks like efficiency pressure driven by higher bids/velocity rather than rank loss or budget caps, **rather than** a drop in relevance (since **CTR** and **search_rank_lost_is** are stable-to-improving).",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$1,149.73",
                        "change": "+17.27%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "47",
                        "change": "+9.30%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "3,814",
                        "change": "+3.97%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "1.23%",
                        "change": "+0.04%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Impression Share",
                        "value": "63.29%",
                        "change": "+1.84%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Rank Lost IS",
                        "value": "36.71%",
                        "change": "-1.83%",
                        "changeColor": "text-green-600"
                      }
                    ],
                    "header": "This week vs baseline"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "This week shows **spend $1149.73 vs baseline $980.44 (+17%)**, **CTR** roughly flat (1.23% vs 1.19% baseline), **search_impression_share** slightly higher (63.29% vs 61.46% baseline), and two ad groups have very high max CPCs ($100.00 in ad_group_ids 147365900369 and 147365900329).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Reduce excessive ad-group max CPCs for ad_group_ids 147365900369 and 147365900329 to $25\u2013$30.\n- Audit Google\u2192CRM attribution mapping for 'Competitor_US_Brand' to confirm CRM conversion ingestion.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_US_Brand"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Abnormal Spend"
            }
          ]
        },
        {
          "id": "18fa7111-75e4-4e05-abce-d9a679267437",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Impressions** are rising despite **CTR** falling, indicating Dropping Engagement/CTR.",
                "border_color": "red"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week delivered substantially more **impressions** while **CTR** declined sharply and clicks were slightly down, showing the additional volume is lower-quality traffic. This looks like a relevance/intent problem rather than rank or budget loss, because **search_impression_share** is roughly stable rather than collapsing.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "impressions",
                        "value": "1464",
                        "change": "+46.10%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "1.57%",
                        "change": "-41.20%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "clicks",
                        "value": "23",
                        "change": "-8.90%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "spend",
                        "value": "$985.77",
                        "change": "-8.50%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "search_impression_share",
                        "value": "68.62%",
                        "change": "-3.20%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "The search term \"salesforce cpq\" produced **571 impressions, 17 clicks and $742.23 spend** last week, while several informational queries (e.g., \"what is cpq in salesforce\" \u2014 69 impressions, 0 clicks; \"what is salesforce cpq\" \u2014 57 impressions, 0 clicks) drove additional impressions with no clicks.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Add negative keywords for low-intent informational queries.\n- Isolate 'salesforce cpq' with exact-match/ad group and cap bids.\n- Promote high-intent variants into focused ad group; raise bids.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_CPQ_Salesforce_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "High"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "37aa99f6-a7da-419c-8603-e06f8310d4f3",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Search Rank Lost IS** is rising despite increased **Spend**, indicating Competitor Pressure.",
                "border_color": "red"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Average weekly **Spend** jumped while **Search Rank Lost IS** increased versus the 8\u2011week baseline, which points to competitors outbidding or outranking us rather than a budget cap. This is supported by higher bids on many keywords and zero CRM conversions, *rather than* a budget constraint (budget lost IS = 0) or low bids causing the impression loss.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$961.56",
                        "change": "+77.70%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "19",
                        "change": "+58.33%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "1.48%",
                        "change": "+0.44 pp",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "1,288.00",
                        "change": "+16.16%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Impression Share",
                        "value": "40.26%",
                        "change": "-3.80 pp",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Rank Lost IS",
                        "value": "59.74%",
                        "change": "+3.80 pp",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Competitor_Spiff_US \u2014 Key weekly metrics vs 8\u2011week baseline",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**This week\u2019s Spend ($961.56) is ~77.7% above the 8\u2011week weekly avg ($541.07) and **Search Rank Lost IS** rose to 0.597 from a baseline of 0.559, while Budget Lost IS = 0 (no budget caps detected).**",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Reduce bids ~30% on six high-cost, single-click keywords.\n- Lower or pause low-quality, high-bid keywords until landing experience improves.\n- A/B test +15\u201325% bid increases on select high-quality exact keywords for 7\u201314 days.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_Spiff_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "High"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Competitor Pressure"
            }
          ]
        },
        {
          "id": "35ea5a38-278b-40ac-97a2-450765704bff",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**CTR** is collapsing despite stable **Impression Share**, indicating Dropping Engagement/CTR.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week the campaign's **CTR** dropped substantially vs the 8-week baseline while **Impression Share** stayed ~80%, which points to poorer ad/query relevance or creative engagement rather than auction rank loss (rank-lost IS improved). Conversion data is sparse, falling back to proxy KPIs.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$665.39",
                        "change": "+18.80%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "4",
                        "change": "-20.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "308",
                        "change": "-25.10%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR",
                        "value": "0.69%",
                        "change": "-1.01pp",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Conversions (CRM)",
                        "value": "0",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search budget lost IS",
                        "value": "17.78%",
                        "change": "+4.66pp",
                        "changeColor": "text-orange-600"
                      }
                    ],
                    "header": "Driving metrics (vs 8\u2011wk baseline)",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**CTR** fell from ~1.71% (8\u2011wk weekly avg) to ~0.69% last week (-1.01pp); **cost** rose from ~$560.24 to $665.39 (+18.8%); the top search term `quotapath` accounted for $488.56 (\u224873% of this week's spend); **Search Rank Lost IS** improved (6.46% \u2192 1.26%).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Set a campaign-level CPC ceiling to cap excessive CPCs.\n- Isolate `quotapath` in its own ad group and lower bids.\n- Switch from TARGET_SPEND to MANUAL_CPC until engagement recovers.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_Quotapath_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "United States"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Med"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "f9fdc62e-c6ad-4f19-a91c-bfb69945355c",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Search Impression Share** is down despite **CTR** improving, indicating Competitor Pressure.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week *Search Impression Share* fell while *CTR* rose, which points to lost visibility from rank/competitive pressure rather than falling interest or a budget cutoff. The presence of a CRM SQL during the same week supports continuing targeted bids on the competitor term rather than pausing the campaign.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Impressions",
                        "value": "657",
                        "change": "-42.19%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Spend",
                        "value": "$608.32",
                        "change": "-14.50%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "CTR",
                        "value": "2.89%",
                        "change": "+1.63%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Avg CPC",
                        "value": "$32.02",
                        "change": "-35.33%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Impression Share",
                        "value": "77.62%",
                        "change": "-6.14%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Rank Lost IS",
                        "value": "22.38%",
                        "change": "+6.14%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "This week *Search Impression Share* fell to 77.62% versus an 8-week baseline of 83.76% and *Search Rank Lost IS* rose to 22.38% versus a baseline of 16.24%; at the same time *CTR* increased to 2.89% versus a baseline of 1.27%.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Lower ad-group CPC overrides above $80 to a $50 cap.\n- Add misspellings (e.g., \"captive iq\", \"captiviq\") as negatives.\n- Keep exact competitor term; consider modest exact-match bid increases.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_CaptivateIQ_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Competitor Pressure"
            }
          ]
        },
        {
          "id": "b4b1fb3c-7301-4e50-a63c-71d98b7f6efa",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Spend** is scaling sharply despite **limited CRM conversions**, indicating Abnormal Spend.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week **spend** and **impressions** jumped well above the 8-week baseline while **CTR** fell, and only a single CRM SQL was recorded, supporting an efficiency problem. This looks driven by higher volume/ bids rather than by lost rank or a tight budget \u2014 *rather than* impression loss or budget cap being the root cause.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "spend",
                        "value": "$586.26",
                        "change": "+276.22%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "clicks",
                        "value": "42",
                        "change": "+100.00%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "impressions",
                        "value": "170",
                        "change": "+247.66%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "ctr",
                        "value": "24.71%",
                        "change": "-42.47%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "crm_conversions (SQLs)",
                        "value": "1"
                      },
                      {
                        "label": "search_impression_share",
                        "value": "100.00%",
                        "change": "+3.42%",
                        "changeColor": "text-green-600"
                      }
                    ],
                    "header": "Driving metrics \u2014 last week vs 8-week baseline"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Last week the campaign spent $586.26 vs an 8-week weekly average of $155.88 (a 276% increase). The search term \"everstage\" accounted for $539.48 of the week\u2019s spend (\u224892% of campaign spend) and CRM shows a single SQL (property_date_of_sql 2026-01-23) attributed to this campaign.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n- Reduce max CPC on 'everstage' keyword to slow spend.\n- Pause or lower bids on duplicate or low-quality variants.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Brand_Everstage_Phrase_UK"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "United Kingdom"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Med"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Abnormal Spend"
            }
          ]
        },
        {
          "id": "196c245b-fc27-42b8-a3ff-230d203606db",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Search Budget Lost IS** is elevated despite steady **Spend**, indicating Budget Constrained.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week the campaign's **Search Budget Lost IS** rose to 7.48% versus the 8\u2011week baseline 1.17%, while **Spend** remained near the 8\u2011week average, suggesting a daily budget ceiling is limiting impressions rather than rank pressure (Search Rank Lost IS moved slightly lower). Conversion mapping is absent, so conclusions rely on proxy metrics.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend (last week)",
                        "value": "$522.55",
                        "change": "+4.68%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Budget Lost IS (avg last week)",
                        "value": "7.48%",
                        "change": "+6.32pp",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Rank Lost IS (avg last week)",
                        "value": "29.83%",
                        "change": "-2.85pp",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks (last week)",
                        "value": "24",
                        "change": "+2.13%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions (last week)",
                        "value": "533",
                        "change": "+3.36%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "4.50%",
                        "change": "-0.24pp",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics (last week vs baseline)",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Evidence: last week **Search Budget Lost IS** = 7.48% vs 8\u2011week baseline 1.17%; campaign_budget_history shows daily budget = $80 (amount_micros = 80,000,000); aggregated spend on 2026-01-20 reached ~$106.14 (exceeding $80); CRM mapping returned 0 contacts for this campaign.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Increase campaign daily budget to $120 and monitor for 7 days.\n- Enable CRM attribution mapping for this campaign and monitor CRM-attributed SQLs.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "SPM_US_Search_Product_SPM_Exact"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Med"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Budget Constrained"
            }
          ]
        },
        {
          "id": "03ab5d2e-6a18-4794-a9c2-e32e42e60b62",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Spend** is elevated despite **CTR** weakness, indicating Abnormal Spend.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "The campaign spent heavily this week while delivering very low engagement, so the primary problem is **excessive spend** rather than poor demand or tracking; rather than being budget-constrained, the campaign exceeded its implied daily budget while generating **low CTR**. This conclusion is based on observed weekly spend and engagement metrics and the campaign budget record.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Weekly spend",
                        "value": "$514.09"
                      },
                      {
                        "label": "Daily budget (configured)",
                        "value": "$50.00"
                      },
                      {
                        "label": "Clicks (week)",
                        "value": "71"
                      },
                      {
                        "label": "Impressions (week)",
                        "value": "10,023"
                      },
                      {
                        "label": "CTR (week)",
                        "value": "0.71%"
                      },
                      {
                        "label": "Avg CPC (week)",
                        "value": "$7.24"
                      }
                    ],
                    "header": "Driving metrics",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**Weekly spend** was $514.09 (campaign_daily_metrics) while the campaign's configured daily budget is $50 (campaign_budget_history), and **CTR** for the week was low at 0.71% (campaign_daily_metrics), supporting spend-driven inefficiency.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Pause or lower daily budget to stop immediate overspend.\n- Set a conservative CPC/CPV bid ceiling at campaign or ad-group level.\n- Refresh creatives and tighten retargeting audiences to raise CTR.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "YouTube - Retargeting - APS High"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Abnormal Spend"
            }
          ]
        },
        {
          "id": "ad76bac1-138d-40bc-acb5-0ca727756764",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Spend** is increasing despite **CRM conversions** remaining zero, indicating Abnormal Spend.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "In the week starting 2026-01-19 the campaign shows higher **spend** (+20.6%) with rising **clicks** (+38.7%) and **search impression share** (+0.053), while **CRM conversions** are 0. This points to escalating investment without downstream outcomes, rather than rank pressure or budget limits (rank-lost improved), so the immediate issue is efficiency not availability of queries.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$506.74",
                        "change": "+20.60%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "43",
                        "change": "+38.71%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search impression share",
                        "value": "39.87%",
                        "change": "+5.34%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Search rank lost impression share",
                        "value": "60.13%",
                        "change": "-5.34%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "1.50%",
                        "change": "+0.10%",
                        "changeColor": "text-gray-600"
                      },
                      {
                        "label": "CRM conversions",
                        "value": "0"
                      }
                    ],
                    "header": "Driving metrics \u2014 week of 2026-01-19",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Search-term reporting shows **\"anaplan\"** accounted for **28 clicks** and **$213.21 spend** in the week of Jan 19; campaign bidding is set to **MANUAL_CPC** and an exact/brand keyword for **anaplan** exists with a configured bid of **$8.00** (8,000,000 micros).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Reduce bid for exact-match 'anaplan' to ~$4\u2013$5 to curb spend.\n- Add exact-match negatives for low-value terms (e.g., 'anaplan demand planning').\n- Cap keyword bids over $50; reduce to a controlled ceiling (e.g., $20).",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_Anaplan_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "Global"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Acceptable Performance"
            }
          ]
        },
        {
          "id": "47950ea2-5c47-4968-8ae3-e7291130068a",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Spend** is up dramatically despite only a small change in **CTR**, indicating abnormal spend scaling without efficiency gains.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week the campaign's **spend** and **impressions** jumped substantially while **CTR** remained essentially flat, supporting an interpretation of spend scaling without improved engagement. This points to an efficiency problem rather than improved demand or better ad relevance, *rather than* a legitimate uplift in engagement driving more valuable traffic.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "spend",
                        "value": "$495.24",
                        "change": "+287.90%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "clicks",
                        "value": "19",
                        "change": "+245.45%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "impressions",
                        "value": "994",
                        "change": "+282.70%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "ctr",
                        "value": "1.91%",
                        "change": "-9.72%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "avg_cpc",
                        "value": "$26.07",
                        "change": "+12.35%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "search_budget_lost_impression_share (weighted)",
                        "value": "63.38%",
                        "change": "+22.92 p.p.",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Last week the campaign spent **$495.24** vs an 8\u2011week weekly average of **$127.61**, with impressions **994** vs baseline **259.75** and clicks **19** vs baseline **5.50**; **CTR** was essentially flat (0.02 vs 0.02 baseline) and the campaign's **weighted budget-lost IS** rose to **0.63** from **0.40**.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Normalize ad\u2011group CPCs; remove extreme bid outliers and set sensible ceilings.\n- Reduce high\u2011CPC ad\u2011groups (e.g., $30) to mid\u2011range bids temporarily.\n- Consolidate budget into top ad\u2011groups and pause low\u2011volume groups.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "US_Search_Product_Theme_Industry"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Abnormal Spend"
            }
          ]
        },
        {
          "id": "d499622e-d3da-410f-a7fc-f094477174b9",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Spend** is elevated despite stable **CTR** and **Impressions**, indicating Acceptable Performance.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Week-over-week vs the 8\u2011week baseline, **spend** increased while **CTR** and **Impressions** remained broadly steady, which points to cost-side pressure rather than a loss of demand or a sudden drop in relevance; rather than flagging budget or rank loss, the available data points to high CPCs at the keyword level driving the spend increase.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend (last week)",
                        "value": "$447.45",
                        "change": "+13.12%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks (last week)",
                        "value": "22",
                        "change": "+12.82%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions (last week)",
                        "value": "258",
                        "change": "+0.63%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "8.53%",
                        "change": "+0.01%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Avg. CPC (last week)",
                        "value": "$20.34",
                        "change": "+0.29%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Conversions (last week)",
                        "value": "0",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics \u2014 last week vs 8\u2011week baseline"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Compared to the 8\u2011week baseline the campaign spent **$447.45** vs **$395.57**, had **22 clicks** vs **19.5**, **258 impressions** vs **256.38**, **CTR 0.0853** vs **0.0760**, and **0 CRM conversions** vs a baseline **0.25/week**.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Add negative exact-match keywords for low-intent search terms\n- Reduce max CPC for top no-convert keyword from $28 to ~$19.60\n- Test 20\u201340% bid reductions on other high-bid, zero-conversion keywords",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "SPM_US_Search_Product_Territory_Exact"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Acceptable Performance"
            }
          ]
        },
        {
          "id": "83696694-aab6-4cac-8321-507da2f41ea9",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Search Budget Lost IS** rose despite increasing **Spend**, indicating Budget Constrained.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week the campaign's **Search Budget Lost IS** increased while **Spend** rose, implying the campaign is hitting budget caps rather than losing auctions to competitors. This is supported by an improved **Search Rank Lost IS** (so it\u2019s *not* rank pressure), rather than a fall in relevance driving impression loss.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$388.52",
                        "change": "+50.60%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "23",
                        "change": "+52.10%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "2.62%",
                        "change": "+22.95%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Budget Lost IS",
                        "value": "14.11%",
                        "change": "+8.54pp",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Rank Lost IS",
                        "value": "17.18%",
                        "change": "-5.45pp",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CRM conversions",
                        "value": "0",
                        "change": "0",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Driving metrics (last week vs 8\u2011week baseline)",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**Last week spend** = $388.52 vs 8-week baseline $258.07 (+50.6%) and **Search Budget Lost IS** = 14.11% vs baseline 5.58% (absolute +8.54pp); **Search Rank Lost IS** fell to 17.18% from baseline 22.63% (improved rank).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Increase campaign daily budget to remove peak-hour caps.\n- Test Manual CPC or Maximize Clicks for two-week trial.\n- Align CRM attribution for this campaign to capture conversions.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitors_EU_UK"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "Europe"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Budget Constrained"
            }
          ]
        },
        {
          "id": "331f55ed-97f3-4a69-b471-b159ffeea258",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Low CTR** despite **moderate spend**, indicating engagement shortfall for this retargeting campaign.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "The campaign delivered a **CTR** far below portfolio norms while running at a middle spend level, so the problem looks like weak engagement rather than a budget issue. This is assessed **rather than** a budget constraint because spend is in the middle band while CTR sits in the bottom_30% (and there are no CRM conversions recorded).",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "CTR",
                        "value": "0.77%"
                      },
                      {
                        "label": "Spend (last week USD)",
                        "value": "$373.20"
                      },
                      {
                        "label": "Clicks (last week)",
                        "value": "37"
                      },
                      {
                        "label": "Impressions (last week)",
                        "value": "4,834"
                      },
                      {
                        "label": "CRM conversions (last week)",
                        "value": "0"
                      }
                    ],
                    "header": "Driving metrics (last week)"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**This week:** 37 clicks, 4,834 impressions, $373.20 spend, **CTR 0.77%**, and **0 CRM conversions**; there is no 8-week baseline available because the campaign first appears on 2026-01-19.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Allow an initial learning period; run 2\u20134 more weeks.\n- Fix CRM attribution mapping; normalize campaign name or add UTMs.\n- If CTR \u22640.8% after learning, pause or narrow audience.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "YouTube - Retargeting - APS Medium/Low"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "ae71ec12-8d05-4149-acea-c98594ebe6ae",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Clicks** increased despite a slightly lower **CTR**, indicating Acceptable Performance.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "The campaign delivered higher **clicks** while **CTR** moved marginally lower and weekly **spend** is in line with the 8\u2011week average, so performance shows traffic stability rather than a clear failure. Conversion data is **sparse/absent** (no CRM conversions), so this conclusion relies on proxy metrics rather than CRM outcomes, and the issue appears *not* to be budget constrained given the daily budget and under\u2011cap spending.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Clicks",
                        "value": "151",
                        "change": "+16.70%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "13,030",
                        "change": "-2.60%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Spend (USD)",
                        "value": "$158.09",
                        "change": "+3.60%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "0.01159",
                        "change": "-0.00067",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "CRM conversions (HubSpot)",
                        "value": "0",
                        "change": "0%",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Driving metrics (vs 8\u2011week avg)"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**Clicks** (151 vs 8\u2011wk avg 129.38, +16.7%) with **CTR** (0.01159 vs 0.01226, -0.00067) and **spend** (158.09 vs 152.67, +3.6%) over the same 8\u2011week baseline; **CRM conversions** = 0 across the period.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Test MAXIMIZE_CLICKS bidding for 14 days to prioritize traffic.\n- If no CRM conversions, pause campaign or reallocate $30/day.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Dispaly-CustomAudience-G2"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Acceptable Performance"
            }
          ]
        },
        {
          "id": "2cd129d5-a34e-41e6-9b9d-bd958452e9e6",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Search Impression Share** is down despite **Spend** increasing, indicating Competitor Pressure.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week the campaign's **Spend** and engagement (Clicks / CTR) rose sharply versus the 8\u2011week baseline while **Search Impression Share** fell and **Search Rank Lost IS** increased, consistent with rivals bidding more aggressively rather than a genuine uplift in demand. This pattern points to competitive rank pressure rather than impression\u2011level demand growth.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$155.55",
                        "change": "+90.20%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "4",
                        "change": "+87.79%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "0.60%",
                        "change": "+90.62%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "672",
                        "change": "-1.39%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Impression Share",
                        "value": "50.90%",
                        "change": "-5.04%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Rank Lost IS",
                        "value": "49.10%",
                        "change": "+6.05%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics (this week vs baseline)",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "This week the campaign spent **$155.55** vs an 8\u2011week weekly average of **$81.76**; weekly **CTR** rose to **0.00595** from **0.00312**, while **Search Impression Share** fell to **0.509** from **0.536** and **Search Rank Lost IS** rose to **0.491** from **0.463**.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Pause or reduce bids for the 'callidus' keyword.\n- Switch 'callidus software' to exact or phrase match and raise bid.\n- Set keyword-level max CPC caps and monitor Rank Lost IS for 7 days.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_SAP_All_Regions"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "All_Regions"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Med"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Competitor Pressure"
            }
          ]
        },
        {
          "id": "0636526b-3778-429c-b736-9e49f936970a",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Spend** is scaling faster than **Impressions** despite higher **CTR**, indicating Abnormal Spend.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week the campaign delivered materially higher **spend** while **impressions** fell and **CTR** rose, which points to rising cost-per-impression/click rather than a demand uplift. This looks like efficiency pressure rather than a simple budget cap issue, *rather than* a budget-constrained or purely engagement problem (budget was $50/day and not exhausted).",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "spend (last week)",
                        "value": "$149.62",
                        "change": "+123.90%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "impressions (last week)",
                        "value": "970",
                        "change": "-52.07%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "5.77%",
                        "change": "+1.87pp",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "clicks (last week)",
                        "value": "56",
                        "change": "-29.11%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "conversions (Google Ads, last week)",
                        "value": "2",
                        "change": "-75.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "search_rank_lost_impression_share (last week)",
                        "value": "35.55%",
                        "change": "+8.30pp",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "This analysis uses the available prior week as the baseline (only one prior week available). Evidence: **spend** rose to $149.62 vs baseline $66.85 (+123.90%), **impressions** fell to 970 vs 2,023 (-52.07%), **CTR** increased to 5.77% vs 3.91% (+1.87pp), and Google Ads conversions fell to 2 vs 8 (-75.00%).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Temporarily reduce daily budget (e.g., $50\u2192$30) while you investigate.\n- Import/link CRM conversions to Google Ads to improve optimization signals.\n- Apply a conservative CPC bid ceiling (e.g., $2\u2013$4) to curb costly clicks.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "PMax_US_2"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Abnormal Spend"
            }
          ]
        },
        {
          "id": "8bec36da-58e8-4d29-a2d0-d8970fa8eb11",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Impressions** are up sharply despite **CTR** collapsing, indicating Dropping Engagement/CTR.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "During the week of Jan 19\u201325 the campaign delivered a large rise in **Impressions** while **CTR** fell substantially and **Spend** remained flat, which points to a sudden influx of lower-quality or broader traffic rather than a budget-driven change. CRM conversions are still absent, so we cannot rely on conversion signals to justify the increased volume.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Impressions (last week)",
                        "value": "24,176",
                        "change": "+111.18%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "0.17%",
                        "change": "-64.79%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Clicks (last week)",
                        "value": "42",
                        "change": "-12.97%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Spend (last week, USD)",
                        "value": "$149.31",
                        "change": "-0.68%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "CRM conversions (last week)",
                        "value": "0",
                        "change": "0%",
                        "changeColor": "text-gray-600"
                      },
                      {
                        "label": "Declared daily budget (USD)",
                        "value": "$30",
                        "change": "0%",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Video Conversions - YouTube In-Stream #2 \u2014 Key metrics (last week vs 8\u2011week avg)",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Evidence: Jan 19\u201325 impressions = **24,176** (+111% vs 8\u2011week avg **11,450**), CTR = **0.17%** (absolute drop ~**0.319** percentage points vs 8\u2011week avg **0.4926%**), and spend \u2248 **$149.31** (flat versus 8\u2011week avg **$150.33**).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Exclude high-impression, low-CTR placements (CTR < 0.5%).\n- Switch bidding to Target CPM/CPV or Maximize video views.\n- Import CRM conversions and map SQLs; allow 2\u20134 weeks to learn.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Video Conversions - YouTube In-Stream #2"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Med"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "b575891e-08de-4caa-9e28-af4828ba12e8",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Impressions** are down despite stable **Spend**, indicating Dropping Impressions driven by softer query volume.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Weekly impressions fell while spend and engagement (CTR) remained essentially unchanged, pointing to a volume/demand drop rather than budget or rank issues. This is supported by zero budget/rank lost IS values and by campaign-level spend being close to prior weeks, *rather than* a clear bidding or budget failure.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Impressions",
                        "value": "8,839",
                        "change": "-17.46%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Spend",
                        "value": "$135.27",
                        "change": "-2.03%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "79",
                        "change": "-15.51%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "CTR",
                        "value": "0.89%",
                        "change": "-0.14%",
                        "changeColor": "text-gray-600"
                      },
                      {
                        "label": "Search budget lost IS (avg)",
                        "value": "0",
                        "change": "0.00%",
                        "changeColor": "text-gray-600"
                      },
                      {
                        "label": "CRM SQLs (contact.property_date_of_sql mapped)",
                        "value": "1",
                        "progress": "1 vs 0",
                        "changeColor": "text-green-600"
                      }
                    ],
                    "header": "Driving metrics"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Week of 2026-01-18: **Impressions 8,839 vs 8-week avg 10,709 (-17%)**, **Spend $135.27 vs $138.06 (~-2%)**, **CTR ~0.89% (flat)**, and **CRM SQLs = 1 vs baseline 0**; search budget/rank lost IS values are 0 for the same period.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Import CRM SQLs as conversions into Google Ads and mark for bidding.\n- Increase daily budget ~25% for 7 days and monitor impressions.\n- Broaden audience targeting slightly for 2 weeks; monitor CTR and impressions.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Demand Gen \u2013 Everstage"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Med"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Impressions"
            }
          ]
        },
        {
          "id": "edbec5bb-d11d-42f1-ab10-9ed696286f08",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Spend** is down despite higher **Clicks** and impressions, indicating Abnormal Spend.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week the campaign delivered materially more **Clicks** and **Impressions** while **Spend** fell vs the 8-week baseline, pointing to an unusual delivery/bidding shift rather than soft demand. Because CRM conversion density is **none**, we cannot confirm business outcomes and must treat this as an efficiency/measurement anomaly rather than a positive efficiency win.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Clicks (last week)",
                        "value": "143",
                        "change": "+156.80%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions (last week)",
                        "value": "9,189",
                        "change": "+143.30%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Spend (last week, USD)",
                        "value": "$98.97",
                        "change": "-56.40%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Avg CPC (last week, USD)",
                        "value": "$0.69",
                        "change": "-83.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "1.56%",
                        "change": "+0.00%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Conversions (last week, Google)",
                        "value": "0",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics (last week vs 8-week baseline)",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "This week: **Spend $98.97 vs 8-week avg $226.75**, **Clicks 143 vs avg 55.7**, **Impressions 9,189 vs avg 3,778**, and **Avg CPC ~$0.69 vs baseline ~$4.07** \u2014 campaign bidding is recorded as **MAXIMIZE_CONVERSIONS** while CRM conversion density is **none**.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n- Switch to MAXIMIZE_CLICKS or conservative bidding temporarily.\n- Set campaign CPC ceiling (e.g., $2.00) to limit low-value clicks.\n- Map Google conversions to CRM or pause conversion bidding.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "PMax_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Abnormal Spend"
            }
          ]
        },
        {
          "id": "454d7f94-7339-488f-9480-8293c9e2af6a",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**CTR** is sharply down despite higher **Impressions**, indicating Dropping Engagement/CTR.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This campaign shows a large fall in **CTR** and **Clicks** while **Impressions** and **Search Impression Share** remain stable, pointing to an engagement/relevance problem rather than a supply or rank issue. The data rule out budget pressure because **Spend** is lower than the 8-week average and **Search Rank Lost IS** did not increase, rather than budget or auction rank being the primary cause.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Impressions (last week)",
                        "value": "711",
                        "change": "+28.70%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks (last week)",
                        "value": "1",
                        "change": "-65.20%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Spend (USD, last week)",
                        "value": "$95.58",
                        "change": "-16.30%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "0.14%",
                        "change": "0.40 percentage points (absolute)",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Impression Share (last week)",
                        "value": "59.52%",
                        "change": "0.56 percentage points (absolute)",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Search Rank Lost IS (last week)",
                        "value": "32.03%",
                        "change": "3.60 percentage points (absolute)",
                        "changeColor": "text-green-600"
                      }
                    ],
                    "header": "Driving metrics (last week vs baseline)",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "The top search term this week was **\"performio\"** which consumed **$95.58** for 1 click (no CRM conversions), and competitor keywords like **\"Spiff\" (235 imp, 0 clicks)**, **\"xactly\" (180 imp, 0 clicks)**, **\"captivateiq\" (119 imp, 0 clicks)** and **\"Varicent\" (120 imp, 0 clicks)** produced impressions but zero clicks.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Lower Performio ad group max CPC to ~30M\u201350M micros.\n- Pause or cut bids for Spiff, CaptivateIQ, Varicent, Xactly ad groups.\n- Switch off Maximize Conversions; use Manual CPC until CRM signal exists.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitors_UK/I"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "United Kingdom"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "d9cbaa01-903e-41a5-b614-90d4269b3a47",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "Stable **Spend** and **Impressions** despite **0 conversions**, indicating Acceptable Performance.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week's **Spend** and **Impressions** are within ~1% of the 8-week averages while **Conversions** remain at zero, so this looks like a functioning top-of-funnel display campaign focused on reach rather than a failing campaign\u2014**rather than** a budget or pacing issue that would show large spend variance.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Clicks (last week)",
                        "value": "403",
                        "change": "-1.5%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Impressions (last week)",
                        "value": "1,119,708",
                        "change": "+1.1%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Spend (last week USD)",
                        "value": "$70.41",
                        "change": "-1.1%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "0.03%",
                        "change": "+0.01%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Conversions (last week)",
                        "value": "0",
                        "change": "0%",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Driving metrics (last week vs 8\u2011week avg)"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Campaign is a DISPLAY campaign using MANUAL_CPM. Last week: **Spend $70.41 vs 8\u2011week avg $71.18**, **Impressions 1,119,708 vs 8\u2011week avg 1,107,216 (+1.1%)**, **Clicks 403 vs 8\u2011week avg 409 (-1.5%)**, **CTR 0.00033 vs baseline 0.0002066**, and **0 conversions** across the period.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Maintain MANUAL_CPM to preserve reach-focused delivery.\n- Add a view-through or engagement conversion action to measure upper-funnel impact.\n- Create a 7\u201330 day retargeting audience from engaged users for lower-funnel campaigns.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "ToFu_Display_CustomIntent_Competitor"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Medium"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Acceptable Performance"
            }
          ]
        },
        {
          "id": "e38cb2b3-0e15-4002-a9b1-eea56f86c7bd",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**CTR** is slightly above the 8-week baseline despite **CRM conversions** remaining at zero, indicating Acceptable Performance.",
                "border_color": "gray"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Weekly engagement (CTR and clicks) is stable-to-better vs the 8-week baseline while spend and impressions are essentially flat, so there is no urgent operational failure. Rather than budget or rank constraints, the core limit is **measurement** / lack of CRM signal (no recorded conversions), which prevents evaluation of downstream value.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "CTR",
                        "value": "0.03%",
                        "change": "5.6%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "144",
                        "change": "4.6%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "537,273",
                        "change": "-1.0%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Spend (USD)",
                        "value": "$69.06",
                        "change": "-1.2%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "CRM conversions",
                        "value": "0",
                        "change": "0%",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Driving metrics vs 8-week baseline",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**Last week:** impressions 537,273 vs 8-week avg 542,578; clicks 144 vs 8-week avg 137.6; spend $69.06 vs 8-week avg $69.87; CRM conversions 0 (no change).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Pause or reallocate this TOFU display campaign.\n- Run 2-week A/B test: MANUAL_CPM vs MAXIMIZE_CLICKS.\n- Fix CRM attribution and enable measurable conversion tracking.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "TOFU_CustomIntent_Display_Global"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Acceptable Performance"
            }
          ]
        },
        {
          "id": "ccdf298e-c114-4a7a-a779-4f4f66156dea",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**CTR** is down despite **stable clicks and spend**, indicating Dropping Engagement/CTR.",
                "border_color": "gray"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week the campaign's **CTR** fell while weekly **clicks** (3) and **spend** (~$58.47) stayed near the 8-week average, so the primary problem is lower engagement per impression rather than immediate budget or spend shifts. This points to an engagement quality issue rather than being **budget constrained**, since **search_budget_lost_impression_share** is 0.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "CTR",
                        "value": "2.97%",
                        "change": "-33.44%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Clicks (weekly)",
                        "value": "3"
                      },
                      {
                        "label": "Spend (weekly USD)",
                        "value": "$58.47",
                        "change": "+4.60%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions (weekly)",
                        "value": "101",
                        "change": "+74.53%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "search_budget_lost_impression_share",
                        "value": "0"
                      }
                    ],
                    "header": "Driving metrics",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "This week **CTR** dropped to ~2.97% vs an 8\u2011week weekly average of ~4.46%, while **clicks** remained 3 vs an 8\u2011week weekly average of 3 and **spend** was $58.47 vs an 8\u2011week weekly average of $55.90; **search_budget_lost_impression_share** is 0 (no budget lost).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Reduce CPC on low-quality keywords to $15 for QS \u2264 2.\n- Add exact negative term \"sales and trading application tracker\".\n- Lower bids for QS=1 keywords to $12\u201315 to limit costly clicks.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "SPM_US_Search_Product_Quota_Exact"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "0234b8c3-a96d-4a05-9691-a1f15f7578a0",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**CTR** is sharply lower despite stable **impression share**, indicating Dropping Engagement/CTR.",
                "border_color": "gray"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week the campaign's **CTR** fell substantially while **impression share** remained near the 8\u2011week baseline, so the problem is poor engagement rather than a loss of query volume or broad budget throttling. This rules out a demand/drop-in-impressions explanation and points to keyword/ad relevance or landing experience as the likely cause, rather than impression scarcity.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Spend",
                        "value": "$56.56",
                        "change": "-68.79%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "1",
                        "change": "-74.19%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "773",
                        "change": "+5.44%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "0.13%",
                        "change": "-74.35%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Impression Share",
                        "value": "57.37%",
                        "change": "-2.51%",
                        "changeColor": "text-orange-600"
                      },
                      {
                        "label": "Search Rank Lost IS",
                        "value": "42.63%",
                        "change": "+3.59%",
                        "changeColor": "text-green-600"
                      }
                    ],
                    "header": "Driving metrics",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Last week CTR was **0.00129** vs the 8\u2011week baseline **0.00503**, impressions were **773** vs baseline **733**, and spend was **$56.56** vs baseline **$181.19** \u2014 showing engagement fell while share of impressions stayed near baseline.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Add phrase/exact negative keywords for high\u2011impression zero\u2011click queries.\n- Pause or lower bids on enabled keywords with very low quality scores.\n- Shift Manual CPC spend to keywords/ad groups that recently produced clicks.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_Xactly_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "39ac17f0-838c-49ed-bac4-210b4dbb1448",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Impression Share** is down despite stable **CTR** and **Spend**, indicating Competitor Pressure.",
                "border_color": "gray"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Search impression share fell while search rank-lost IS rose, which points to losing auctions rather than a budget limit. This pattern is driven by higher **search_rank_lost_impression_share** even though **CTR** and **Spend** are essentially unchanged, so the likely cause is competitive rank pressure rather than budget constraints.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Search impression share",
                        "value": "53.08%",
                        "change": "-5.71%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search rank-lost impression share",
                        "value": "46.92%",
                        "change": "+5.71%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "2.13%",
                        "change": "+0.03%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Spend",
                        "value": "$38.11",
                        "change": "+1.82%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Impressions",
                        "value": "94",
                        "change": "-12.25%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "2",
                        "change": "-11.11%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Driving metrics (vs baseline)"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Last week **search_impression_share** fell to **53.08%** (8\u2011week baseline **58.79%**) while **search_rank_lost_impression_share** rose to **46.92%** (baseline **41.21%**). Campaign budget lost IS is **0%** (budget not limiting) and spend remained ~flat at **$38.11** vs baseline **$37.43**.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Increase ad group CPC for Qobra keywords to recover rank\n- Cap or reduce extreme bids on unrelated high-cost competitor keywords\n- Improve landing relevance to raise Quality Score for Qobra keywords",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_Qobra_US"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Competitor Pressure"
            }
          ]
        },
        {
          "id": "496cb545-7bc2-4304-9f2e-866491f84c4b",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Impressions** rose sharply despite **stable spend**, indicating the campaign is Budget Constrained.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "The campaign showed a large increase in weekly **impressions** while **spend** remained essentially flat, consistent with a budget cap limiting delivery. Conversion data is sparse, falling back to proxy KPIs; this looks like a budget/coverage issue rather than a relevance problem, rather than rank pressure or a sudden bid change.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Impressions",
                        "value": "266",
                        "change": "+86.70%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Spend",
                        "value": "$30.09",
                        "change": "+0.01%",
                        "changeColor": "text-gray-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "7",
                        "change": "+16.70%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "CTR",
                        "value": "2.63%",
                        "change": "-0.03 (absolute change)",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search budget lost IS",
                        "value": "81.64%",
                        "change": "-0.03 (absolute change)",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "Conversions (Google Ads)",
                        "value": "0",
                        "change": "0%",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Driving metrics \u2014 last week vs baseline",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**Search Budget Lost IS ~82% (last week) vs ~84.7% baseline** and the campaign budget is set to **$5/day** (amount_micros = 5,000,000), while impressions jumped ~86.7% but weekly spend stayed ~flat at ~$30, indicating delivery is capped by budget.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Increase daily budget; test 3x for one week and monitor.\n- Lower 'commission' keyword max CPC to $5\u2013$10.\n- Reduce Product ad group max CPC to rebalance spend.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "RLSA_Grouped"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Budget Constrained"
            }
          ]
        },
        {
          "id": "92f3d3ff-dd6f-426d-a66a-6952fa7cf4e8",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Impressions** have collapsed despite only a moderate reduction in **Spend**, indicating Dropping Impressions.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "This week **impressions** and **clicks** fell dramatically while **spend** declined only moderately, pointing to a loss of eligible audience or targeting changes rather than budget limits. Note: the baseline here is the single prior full week available because the campaign started on 2026-01-13, so conclusions are calibrated to that limited history.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "Impressions",
                        "value": "390",
                        "change": "-92.30%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Clicks",
                        "value": "1",
                        "change": "-99.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Spend",
                        "value": "$25.85",
                        "change": "-57.60%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR",
                        "value": "0.26%",
                        "change": "-1.62 percentage points",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CRM conversions (CRM)",
                        "value": "0",
                        "change": "0.00%",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Driving metrics"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "**Impressions**: 390 vs baseline 5,054; **Clicks**: 1 vs baseline 95; **Spend**: $25.85 vs baseline $60.88 (week starting 2026-01-12).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Restore/confirm intended geo-targeting state\n- Expand remarketing audience; extend membership window for video ad group",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "YouTube - Retargeting - APS P0"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "Asia Pacific"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Impressions"
            }
          ]
        },
        {
          "id": "274b66e9-8af6-474d-8b73-86b1cb0408b7",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**CTR** is down despite higher **impressions**, indicating Dropping Engagement/CTR.",
                "border_color": "gray"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Last week the campaign's **CTR** fell while **impressions** increased and **spend** decreased versus the 8-week baseline, which points to lower user engagement rather than a budget or impression supply problem. This is supported by a near-zero **search_budget_lost_impression_share**, so the drop is likely engagement-driven rather than budget-constrained.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "clicks (last week)",
                        "value": "1.00",
                        "change": "-38.50%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "impressions (last week)",
                        "value": "60.00",
                        "change": "77.80%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "spend (last week USD)",
                        "value": "$19.48",
                        "change": "-36.30%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "1.67%",
                        "change": "-1.52 pp",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "search_impression_share (avg last week)",
                        "value": "84.05%",
                        "change": "-2.31 pp",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "search_budget_lost_impression_share (last week)",
                        "value": "0.00%",
                        "change": "0.00 pp",
                        "changeColor": "text-gray-600"
                      }
                    ],
                    "header": "Driving metrics \u2014 last week vs 8-week baseline"
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Compared with the 8-week weekly average, **CTR** dropped by ~1.52 percentage points while **impressions** rose ~77.8% and **spend** fell ~36.3%; conversions remained 0 for both the week and baseline.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Reduce CPC bids for low-quality keywords to $5\u2013$8.\n- Create a new ad variation emphasizing 'sales capacity planning' and test for 2 weeks.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "SPM_US_Search_Product_Capacity_Exact"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "b4c8b8cf-1456-41c2-83bf-b174f96ef1cd",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**CTR** fell to **0** despite near-baseline **impressions**, indicating Dropping Engagement/CTR.",
                "border_color": "gray"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Weekly data shows clicks and spend collapsed to zero while impressions remained close to the 8-week average, pointing to an engagement breakdown (ads/creatives or relevance) rather than a budget cut. Given this is a competitor campaign, the loss of clicks with impressions intact suggests relevance/creative or query mismatch is the primary issue.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "clicks (last week)",
                        "value": "0",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "spend_usd (last week)",
                        "value": "$0.00",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "ctr (last week)",
                        "value": "0.00%",
                        "change": "+4.69%",
                        "changeColor": "text-green-600"
                      },
                      {
                        "label": "impressions (last week)",
                        "value": "38",
                        "change": "-9.26%",
                        "changeColor": "text-orange-600"
                      }
                    ],
                    "header": "Driving metrics",
                    "columns": 2
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Last week produced **0 clicks vs 1.75 avg**, **$0 spend vs $18.01 avg**, **CTR 0% vs ~4.69% baseline**, and **38 impressions vs 41.9 avg**.",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Add exact-match negative keywords for irrelevant queries (e.g., 'spiff oeksound review').\n- Lower bids on high-CPC Spiff_phrase ad groups to conserve spend.\n- Pause low-CTR competitor keyword variants and test before re-enabling.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_Alternatives_EU_UK"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "Europe"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Engagement/CTR"
            }
          ]
        },
        {
          "id": "8a58060a-545a-4ed5-82d1-32e66de9cd6c",
          "blocks": [
            {
              "id": "eb6c00c1-b09e-4710-9804-252b269e1df2",
              "config": {
                "heading": "**Impressions** fell to zero despite **Budget Lost IS** remaining at 0, indicating Dropping Impressions driven by elevated **Search Rank Lost IS**.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e",
                  "type": "text",
                  "content": "Weekly activity went from an 8\u2011week average of **~5.5 impressions/week** to **0 impressions** while **Budget Lost IS** stayed at **0**, pointing to a rank/eligibility issue rather than budget. This is supported by the jump in **Search Rank Lost IS** to **~90%**, rather than a budget cap or account-level serving pause.",
                  "version": "v1"
                },
                {
                  "id": "722e1c10-f227-4e02-baae-532ade5c77d6",
                  "type": "metrics_grid",
                  "content": {
                    "cells": [
                      {
                        "label": "impressions (last week)",
                        "value": "0",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "clicks (last week)",
                        "value": "0",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "spend (USD, last week)",
                        "value": "$0.00",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "CTR (last week)",
                        "value": "0.00%",
                        "change": "-100.00%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Impression Share (last week)",
                        "value": "9.99%",
                        "change": "-67.24%",
                        "changeColor": "text-red-600"
                      },
                      {
                        "label": "Search Rank Lost IS (last week)",
                        "value": "90.01%",
                        "change": "+100.36%",
                        "changeColor": "text-red-600"
                      }
                    ],
                    "header": "Key driving metrics \u2014 last week vs baseline",
                    "columns": 3
                  },
                  "version": "v1"
                },
                {
                  "id": "a9b8c7d6-e5f4-4a3b-2c1d-0e9f8a7b6c5d",
                  "type": "text",
                  "content": "Historical weekly baseline vs last week: average weekly impressions were **~5.5** and spend **~$2.04**, while last week impressions and spend were **0**; **Search Rank Lost IS** rose from an 8\u2011week average of **~44.9%** to **~90.0%** last week (and **Search Budget Lost IS** was **0** last week).",
                  "version": "v1"
                },
                {
                  "id": "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a",
                  "type": "text",
                  "content": "### Recommended Actions\n\n- Set conservative non-zero keyword max CPCs for enabled competitor keywords.\n- Set non-zero ad-group level CPCs so keywords inherit bids quickly.\n- Audit and remove negative keywords blocking competitor-phrase matches.",
                  "version": "v1"
                }
              ]
            }
          ],
          "properties": [
            {
              "id": "72953935-77ae-41f3-8753-f19146feb19e",
              "value": "Competitor_Alternatives_CA"
            },
            {
              "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
              "value": "North America"
            },
            {
              "id": "4b14a51c-5c7f-4a89-8e7a-defd2bcd50d1",
              "value": "Low"
            },
            {
              "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
              "value": "Dropping Impressions"
            }
          ]
        }
      ],
      "layouts": [
        {
          "id": "b353d30b-b548-4e82-bc2f-01fc55ab1736",
          "name": "Campaign Explorer",
          "type": "explorer",
          "config": {
            "sidebar": {
              "group": {
                "property": {
                  "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c"
                }
              }
            },
            "top_filter": {
              "properties": [
                {
                  "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3"
                },
                {
                  "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c"
                }
              ]
            }
          }
        }
      ],
      "properties": [
        {
          "id": "72953935-77ae-41f3-8753-f19146feb19e",
          "display_name": "Campaign"
        },
        {
          "id": "ce07bd64-cd6f-47e7-a156-b8335cbe02f3",
          "display_name": "Region"
        },
        {
          "id": "f7a8b9c0-d1e2-4f3a-5b6c-7d8e9f0a1b2c",
          "display_name": "Performance Characteristic"
        }
      ]
    },
    {
      "id": "pg-portfolio-overview",
      "display_name": "Portfolio Summary",
      "pages": [
        {
          "id": "portfolio-page-1",
          "blocks": [
            // Summary section — 2-line summary + description + metric grid
            {
              "id": "portfolio-summary-text",
              "type": "text" as const,
              "version": "v1" as const,
              "content": "## Google Ads Portfolio — Weekly Performance Review\nWeek of Jan 19–25, 2026 · 12 active campaigns across 3 regions"
            },
            {
              "id": "portfolio-summary-desc",
              "type": "text" as const,
              "version": "v1" as const,
              "content": "Overall portfolio health is **strong** with 78% of campaigns meeting targets. Total spend is within budget at 92% utilization, while ROAS improved 12% versus the prior period. Brand Search and Product DSA campaigns lead performance, while Non-Brand segments face rising CPAs due to increased competitor activity."
            },
            {
              "id": "portfolio-metrics-1",
              "type": "metrics_grid" as const,
              "version": "v1" as const,
              "content": {
                "cells": [
                  { "label": "Total Spend", "value": "$45,230", "change": "+8.2%", "changeColor": "text-green-600" },
                  { "label": "ROAS", "value": "4.2x", "change": "+12.0%", "changeColor": "text-green-600" },
                  { "label": "Conversions", "value": "1,847", "change": "+15.3%", "changeColor": "text-green-600" },
                  { "label": "CPA", "value": "$24.50", "change": "-5.1%", "changeColor": "text-green-600" },
                  { "label": "CTR", "value": "3.82%", "change": "+0.24 pp", "changeColor": "text-green-600" },
                  { "label": "Impression Share", "value": "68.4%", "change": "-1.8 pp", "changeColor": "text-orange-600" }
                ],
                "header": "Portfolio metrics — this week vs 8-week baseline",
                "columns": 3
              }
            },
            // Tabbed charts section — trends across key metrics
            {
              "id": "portfolio-tabbed-chart",
              "type": "tabbed_chart" as const,
              "version": "v1" as const,
              "content": {
                "title": "Portfolio Trends",
                "description": "Key metrics over the last 8 weeks",
                "defaultTabId": "spend",
                "height": 300,
                "tabs": [
                  {
                    "id": "spend",
                    "label": "Spend",
                    "chartType": "area" as const,
                    "chartProps": {
                      "data": [
                        { "week": "Dec 1", "brand": 12400, "nonBrand": 18200, "competitor": 8900 },
                        { "week": "Dec 8", "brand": 11800, "nonBrand": 17500, "competitor": 9100 },
                        { "week": "Dec 15", "brand": 13100, "nonBrand": 19400, "competitor": 9600 },
                        { "week": "Dec 22", "brand": 10500, "nonBrand": 15200, "competitor": 7800 },
                        { "week": "Dec 29", "brand": 11200, "nonBrand": 16800, "competitor": 8200 },
                        { "week": "Jan 5", "brand": 13800, "nonBrand": 19800, "competitor": 9900 },
                        { "week": "Jan 12", "brand": 14200, "nonBrand": 20100, "competitor": 10300 },
                        { "week": "Jan 19", "brand": 14800, "nonBrand": 20800, "competitor": 9630 }
                      ],
                      "xAxisKey": "week",
                      "areas": [
                        { "dataKey": "brand", "fillOpacity": 0.15, "stackId": "spend" },
                        { "dataKey": "nonBrand", "fillOpacity": 0.15, "stackId": "spend" },
                        { "dataKey": "competitor", "fillOpacity": 0.15, "stackId": "spend" }
                      ],
                      "showLegend": true
                    }
                  },
                  {
                    "id": "conversions",
                    "label": "Conversions",
                    "chartType": "line" as const,
                    "chartProps": {
                      "data": [
                        { "week": "Dec 1", "gadsConv": 142, "crmConv": 38 },
                        { "week": "Dec 8", "gadsConv": 138, "crmConv": 35 },
                        { "week": "Dec 15", "gadsConv": 156, "crmConv": 42 },
                        { "week": "Dec 22", "gadsConv": 118, "crmConv": 28 },
                        { "week": "Dec 29", "gadsConv": 125, "crmConv": 31 },
                        { "week": "Jan 5", "gadsConv": 168, "crmConv": 45 },
                        { "week": "Jan 12", "gadsConv": 174, "crmConv": 48 },
                        { "week": "Jan 19", "gadsConv": 185, "crmConv": 52 }
                      ],
                      "xAxisKey": "week",
                      "showLegend": true
                    }
                  },
                  {
                    "id": "ctr",
                    "label": "CTR",
                    "chartType": "bar" as const,
                    "chartProps": {
                      "data": [
                        { "week": "Dec 1", "ctr": 3.42 },
                        { "week": "Dec 8", "ctr": 3.38 },
                        { "week": "Dec 15", "ctr": 3.61 },
                        { "week": "Dec 22", "ctr": 3.25 },
                        { "week": "Dec 29", "ctr": 3.48 },
                        { "week": "Jan 5", "ctr": 3.72 },
                        { "week": "Jan 12", "ctr": 3.78 },
                        { "week": "Jan 19", "ctr": 3.82 }
                      ],
                      "xAxisKey": "week",
                      "dataKeys": [{ "key": "ctr", "name": "CTR (%)" }],
                      "showLegend": false
                    }
                  },
                  {
                    "id": "impressionShare",
                    "label": "Impression Share",
                    "chartType": "line" as const,
                    "chartProps": {
                      "data": [
                        { "week": "Dec 1", "is": 71.2, "rankLost": 18.5, "budgetLost": 10.3 },
                        { "week": "Dec 8", "is": 70.8, "rankLost": 19.1, "budgetLost": 10.1 },
                        { "week": "Dec 15", "is": 72.5, "rankLost": 17.8, "budgetLost": 9.7 },
                        { "week": "Dec 22", "is": 69.4, "rankLost": 20.2, "budgetLost": 10.4 },
                        { "week": "Dec 29", "is": 70.1, "rankLost": 19.6, "budgetLost": 10.3 },
                        { "week": "Jan 5", "is": 71.8, "rankLost": 18.2, "budgetLost": 10.0 },
                        { "week": "Jan 12", "is": 69.8, "rankLost": 19.5, "budgetLost": 10.7 },
                        { "week": "Jan 19", "is": 68.4, "rankLost": 20.8, "budgetLost": 10.8 }
                      ],
                      "xAxisKey": "week",
                      "showLegend": true
                    }
                  }
                ]
              }
            },
            // Highlights & Lowlights
            {
              "id": "portfolio-highlights",
              "type": "text" as const,
              "version": "v1" as const,
              "content": "### Highlights\n\n- **Brand Search** campaigns hit 88% impression share — highest in 12 weeks\n- **CRM-attributed SQLs** up 37% WoW, driven by Product DSA and Brand Exact campaigns\n- **CPA decreased 5.1%** overall, showing improved spend efficiency across the portfolio\n- **YouTube Retargeting** generated 3 CRM SQLs this week after creative refresh\n\n### Lowlights\n\n- **Competitor campaigns** losing impression share (−3.8 pp) due to rising competitor bids in the SPM and CPQ segments\n- **Non-Brand Phrase US** spending 63% above baseline with zero CRM conversions — efficiency problem\n- **CTR dropping** in 4 of 12 campaigns, notably Competitor_CPQ_Salesforce_US (−41.2% vs baseline)\n- **Budget constraints** emerging in Product CPQ Exact — Budget Lost IS spiked +18.96 pp, capping impressions"
            },
            // Catalog table — links to individual campaign pages
            {
              "id": "portfolio-campaign-catalog",
              "type": "data_table_client" as const,
              "version": "v1" as const,
              "content": {
                "title": "Campaign Performance Overview",
                "description": "Click a campaign to view its detailed analysis",
                "columns": [
                  { "key": "campaign", "label": "Campaign" },
                  { "key": "spend", "label": "Spend" },
                  { "key": "conversions", "label": "Conversions" },
                  { "key": "ctr", "label": "CTR" },
                  { "key": "status", "label": "Status" }
                ],
                "rows": [
                  {
                    "campaign": "US_Search_Product_Theme_Exact",
                    "spend": "$14,800",
                    "conversions": "642",
                    "ctr": "4.51%",
                    "status": "Dropping CTR",
                    "__page_ref": {
                      "page_group_id": "0f63c091-a02e-49a9-8a97-37abfa93596c",
                      "page_id": "231e6dc0-ea81-43d9-90c1-89100eae1ef0"
                    }
                  },
                  {
                    "campaign": "Brand_Everstage_Phrase_US",
                    "spend": "$4,628",
                    "conversions": "384",
                    "ctr": "6.12%",
                    "status": "Abnormal Spend",
                    "__page_ref": {
                      "page_group_id": "0f63c091-a02e-49a9-8a97-37abfa93596c",
                      "page_id": "16720484-39e3-4286-addb-01d0501214d2"
                    }
                  },
                  {
                    "campaign": "APAC_Display_Retargeting",
                    "spend": "$3,400",
                    "conversions": "156",
                    "ctr": "1.82%",
                    "status": "Emerging"
                  },
                  {
                    "campaign": "EU_Brand_Exact_UK",
                    "spend": "$6,210",
                    "conversions": "298",
                    "ctr": "5.78%",
                    "status": "Strong"
                  },
                  {
                    "campaign": "Non_Brand_Phrase_US",
                    "spend": "$9,150",
                    "conversions": "0",
                    "ctr": "2.34%",
                    "status": "Efficiency Problem"
                  },
                  {
                    "campaign": "Competitor_CPQ_Salesforce_US",
                    "spend": "$5,700",
                    "conversions": "112",
                    "ctr": "1.95%",
                    "status": "Dropping CTR"
                  },
                  {
                    "campaign": "Brand_Search_Product_DSA",
                    "spend": "$8,340",
                    "conversions": "520",
                    "ctr": "7.23%",
                    "status": "Strong"
                  },
                  {
                    "campaign": "YouTube_Retargeting_US",
                    "spend": "$2,100",
                    "conversions": "45",
                    "ctr": "0.89%",
                    "status": "Emerging"
                  },
                  {
                    "campaign": "EU_Display_Prospecting_DE",
                    "spend": "$4,350",
                    "conversions": "87",
                    "ctr": "1.12%",
                    "status": "Underperforming"
                  },
                  {
                    "campaign": "Brand_Exact_Everstage_APAC",
                    "spend": "$3,280",
                    "conversions": "201",
                    "ctr": "5.45%",
                    "status": "Strong"
                  },
                  {
                    "campaign": "Non_Brand_Broad_US",
                    "spend": "$11,400",
                    "conversions": "68",
                    "ctr": "1.67%",
                    "status": "High Spend Low Return"
                  },
                  {
                    "campaign": "Competitor_SPM_US",
                    "spend": "$4,890",
                    "conversions": "95",
                    "ctr": "2.10%",
                    "status": "Losing IS"
                  }
                ],
                "enableSearch": true,
                "searchPlaceholder": "Search campaigns...",
                "enablePagination": true,
                "pageSize": 5
              }
            }
          ]
        }
      ]
    },
    {
      "id": "pg-region-summary",
      "display_name": "Region Summary",
      "properties": [
        { "id": "region_name", "display_name": "Region" }
      ],
      "pages": [
        {
          "id": "region-page-na",
          "properties": [
            { "id": "region_name", "value": "North America" }
          ],
          "blocks": [
            {
              "id": "region-block-na",
              "type": "insight_card" as const,
              "config": {
                "heading": "North America leads in **spend efficiency** with the highest ROAS across regions.",
                "border_color": "green"
              },
              "block_elements": [
                {
                  "id": "region-text-na",
                  "type": "text" as const,
                  "content": "North America accounts for **62% of total spend** and delivers the highest ROAS at 4.8x. **Brand search** campaigns are particularly strong with 88% impression share. US markets show consistent growth in conversions (+18% WoW).",
                  "version": "v1" as const
                },
                {
                  "id": "region-metrics-na",
                  "type": "metrics_grid" as const,
                  "content": {
                    "cells": [
                      { "label": "Spend", "value": "$28,042", "change": "+6.5%", "changeColor": "text-green-600" },
                      { "label": "ROAS", "value": "4.8x", "change": "+14.2%", "changeColor": "text-green-600" },
                      { "label": "Conversions", "value": "1,145", "change": "+18.0%", "changeColor": "text-green-600" },
                      { "label": "Impression Share", "value": "82.3%", "change": "+2.1 pp", "changeColor": "text-green-600" }
                    ],
                    "header": "NA regional metrics — this period vs previous",
                    "columns": 4
                  },
                  "version": "v1" as const
                }
              ]
            }
          ]
        },
        {
          "id": "region-page-eu",
          "properties": [
            { "id": "region_name", "value": "Europe" }
          ],
          "blocks": [
            {
              "id": "region-block-eu",
              "type": "insight_card" as const,
              "config": {
                "heading": "European campaigns show **growing momentum** but face higher CPCs.",
                "border_color": "yellow"
              },
              "block_elements": [
                {
                  "id": "region-text-eu",
                  "type": "text" as const,
                  "content": "Europe represents **28% of total spend** with a 3.5x ROAS. **CPCs are 22% higher** than NA due to competitive markets in UK and Germany. France and Spain show the best efficiency within the region.",
                  "version": "v1" as const
                }
              ]
            }
          ]
        },
        {
          "id": "region-page-apac",
          "properties": [
            { "id": "region_name", "value": "Asia Pacific" }
          ],
          "blocks": [
            {
              "id": "region-block-apac",
              "type": "insight_card" as const,
              "config": {
                "heading": "APAC is in **early growth phase** with promising conversion trends.",
                "border_color": "blue"
              },
              "block_elements": [
                {
                  "id": "region-text-apac",
                  "type": "text" as const,
                  "content": "APAC accounts for **10% of total spend** with a 2.8x ROAS. While volumes are lower, **conversion rates are improving** steadily (+25% MoM). Australia and Japan are the primary contributors.",
                  "version": "v1" as const
                }
              ]
            }
          ]
        }
      ]
    }
  ]
};

// Single page group example - simulates old reports where backend sent only 1 group without display_name
// The sidebar should skip group-level grouping and render pages directly
const payloadSinglePageGroup: InsightCardPayloadV2 = {
  "type": "MULTI_PAGE",
  "version": "v2",
  "page_groups": [
    {
      "id": "0f63c091-a02e-49a9-8a97-37abfa93596c",
      // No display_name — simulates old reports before multi-group support
      ...payloadMultiPage.page_groups[0],
      display_name: undefined,
    }
  ]
};

export function InsightsDemo() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(t);
  }, []);

  const handleUIAction = (event: UIActionEvent) => {
    // For demo, just log the action
    // eslint-disable-next-line no-console
    console.log("UI Action:", event);
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <Container padding="lg" width="100%" columns={1} className="w-full">
          <InsightsReportSkeleton />
        </Container>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex flex-col">
      <Tabs defaultValue="multipage" className="w-full flex flex-col flex-1 min-h-0">
        <div className="flex-shrink-0 px-4 pt-4">
          <TabsList className="mb-4">
            <TabsTrigger value="flat">Flat Report</TabsTrigger>
            <TabsTrigger value="multipage">Multi-page Report</TabsTrigger>
            <TabsTrigger value="singlegroup">Single Page Group</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="flat" className="px-4 pb-4">
          <InsightCardPayloadRenderer
            payload={payloadFlat}
            onUIAction={handleUIAction}
            className="w-full"
          />
        </TabsContent>

        <TabsContent value="multipage" className="flex-1 min-h-0">
          <InsightCardMultiPageRenderer
            pageGroups={payloadMultiPage.page_groups}
            onUIAction={handleUIAction}
            className="w-full"
          />
        </TabsContent>

        <TabsContent value="singlegroup" className="flex-1 min-h-0">
          <InsightCardMultiPageRenderer
            pageGroups={payloadSinglePageGroup.page_groups}
            onUIAction={handleUIAction}
            className="w-full"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
